import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserLayout from './UserLayout';
import SearchContacts from './SearchContacts';
import MyContacts from './MyContacts';
import UserProfile from './UserProfile';
import Calendar from './Calendar';
import ContactsMap from './ContactsMap';
import Preferences from './Preferences';
import Messages from './Messages';
import Modules from './Modules';
import Events from './Events';
import EndroitsPage from './EndroitsPage';
import { logout } from '../../services/authService';

const DASHBOARD_PAGE_KEY = 'cabaza_dashboard_page';

/**
 * User Dashboard - Main component for logged-in users
 */
const UserDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get initial page from localStorage (persisted across reloads)
    const getInitialPage = () => {
        // Check if page was passed via navigation state (from OccupationsPage, etc.)
        if (location.state?.page) {
            const validPages = ['search', 'modules', 'contacts', 'messages', 'map', 'profile', 'calendar', 'preferences', 'events', 'places'];
            if (validPages.includes(location.state.page)) {
                return location.state.page;
            }
        }
        const savedPage = localStorage.getItem(DASHBOARD_PAGE_KEY);
        // Valid pages that can be restored
        const validPages = ['search', 'modules', 'contacts', 'messages', 'map', 'profile', 'calendar', 'preferences', 'events', 'places'];
        if (savedPage && validPages.includes(savedPage)) {
            return savedPage;
        }
        return 'search';
    };

    const [currentPage, setCurrentPage] = useState(getInitialPage);

    // Handle navigation state changes (when coming from other pages)
    useEffect(() => {
        if (location.state?.page) {
            const validPages = ['search', 'modules', 'contacts', 'messages', 'map', 'profile', 'calendar', 'preferences', 'events', 'places'];
            if (validPages.includes(location.state.page)) {
                setCurrentPage(location.state.page);
                // Clear the state to prevent re-navigation on refresh
                navigate('/', { replace: true, state: {} });
            }
        }
    }, [location.state, navigate]);

    // Persist current page to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(DASHBOARD_PAGE_KEY, currentPage);
    }, [currentPage]);

    const handleLogout = async () => {
        // Save contacts to server before logout
        await logout();
        // Clear remaining auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem(DASHBOARD_PAGE_KEY);
        window.location.href = '/'; // Force page reload to update auth state
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'search':
                return <SearchContacts />;
            case 'modules':
                return <Modules onNavigate={setCurrentPage} />;
            case 'contacts':
                return <MyContacts />;
            case 'messages':
                return <Messages />;
            case 'map':
                return <ContactsMap />;
            case 'profile':
                return <UserProfile />;
            case 'calendar':
                return <Calendar language="fr" />;
            case 'events':
                return <Events />;
            case 'places':
                return <EndroitsPage />;
            case 'preferences':
                return <Preferences />;
            default:
                return <Modules onNavigate={setCurrentPage} />;
        }
    };

    return (
        <UserLayout
            currentPage={currentPage}
            onNavigate={setCurrentPage}
            onLogout={handleLogout}
        >
            {renderPage()}
        </UserLayout>
    );
};

export default UserDashboard;
